import { useEntities } from "@app/store/entityStore";
import { IconName } from "@design-system/Icon";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import {
    KbDocumentType,
    KnowledgeBaseItemStatus,
} from "@generated/client/graphql";
import { useKnowledgeBaseDropdownFilterTranslations } from "./i18n";

export const DropdownEntityFilter = ({
    selectedEntityIds,
    setSelectedEntityIds,
}: {
    selectedEntityIds: string[];
    setSelectedEntityIds: (entityIds: string[]) => void;
}) => {
    const { entities } = useEntities();

    const { t, keys } = useKnowledgeBaseDropdownFilterTranslations();

    return (
        <Dropdown
            value={selectedEntityIds}
            onValueChange={setSelectedEntityIds}
            selectType="multi"
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.entity, {
                    count: selectedEntityIds.length,
                })}
                options={entities}
            />
            <Dropdown.Content hasPortal align="start">
                <Dropdown.Reset label={t(keys.reset)} />
                {entities?.map((entity) => (
                    <Dropdown.SelectableItem key={entity.id} value={entity.id}>
                        <Dropdown.Checkbox />
                        <Dropdown.ItemText>{entity.name}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};

export const DropdownStatusFilter = ({
    selectedStatuses,
    setSelectedStatuses,
}: {
    selectedStatuses: KnowledgeBaseItemStatus[];
    setSelectedStatuses: (statuses: KnowledgeBaseItemStatus[]) => void;
}) => {
    const { t, keys } = useKnowledgeBaseDropdownFilterTranslations();

    const isStatus = (value: string): value is KnowledgeBaseItemStatus => {
        return Object.values(KnowledgeBaseItemStatus).includes(
            value as KnowledgeBaseItemStatus,
        );
    };

    const tagStatus = (status: KnowledgeBaseItemStatus) => {
        if (status === KnowledgeBaseItemStatus.Done) {
            return "validated";
        }
        if (status === KnowledgeBaseItemStatus.InProgress) {
            return "inProgress";
        }
        return "todo";
    };

    const getStatusTagCircularIcon = (
        status: "todo" | "inProgress" | "validated",
    ): IconName => {
        switch (status) {
            case "todo":
                return "circleTodo";
            case "inProgress":
                return "circleInProgress";
            case "validated":
                return "circleValidated";
        }
    };

    const handleValueChange = (input: string[]) => {
        const validStatuses = input.filter(isStatus);
        setSelectedStatuses(validStatuses);
    };

    const options = Object.values(KnowledgeBaseItemStatus).map((status) => ({
        id: status,
        name: t(status),
        icon: getStatusTagCircularIcon(tagStatus(status)),
    }));

    return (
        <Dropdown
            selectType="multi"
            value={selectedStatuses}
            onValueChange={handleValueChange}
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.status, {
                    count: selectedStatuses.length,
                })}
                options={options}
            />
            <Dropdown.Content hasPortal align="start">
                <Dropdown.Reset label={t(keys.reset)} />
                {options.map((status) => (
                    <Dropdown.SelectableItem key={status.id} value={status.id}>
                        <Dropdown.Checkbox />
                        <Dropdown.ItemIcon name={status.icon} size="md" />
                        <Dropdown.ItemText>{t(status.name)}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};

export const DropdownDocumentTypeFilter = ({
    selectedTypes,
    setSelectedTypes,
}: {
    selectedTypes: KbDocumentType[];
    setSelectedTypes: (types: KbDocumentType[]) => void;
}) => {
    const { t, keys } = useKnowledgeBaseDropdownFilterTranslations();

    const isKbDocumentType = (value: string): value is KbDocumentType => {
        return Object.values(KbDocumentType).includes(value as KbDocumentType);
    };

    const handleValueChange = (input: string[]) => {
        const validTypes = input.filter(isKbDocumentType);
        setSelectedTypes(validTypes);
    };

    const iconToDisplay = (type: KbDocumentType): IconName => {
        if (type === KbDocumentType.Policy) {
            return "scale";
        }
        if (type === KbDocumentType.Measure) {
            return "target";
        }
        return "document";
    };

    const options = Object.values(KbDocumentType).map((type) => ({
        id: type,
        name: t(type),
        icon: iconToDisplay(type),
    }));

    return (
        <Dropdown
            selectType="multi"
            value={selectedTypes}
            onValueChange={handleValueChange}
        >
            <Dropdown.ChipFilterTrigger
                label={t(keys.type, {
                    count: selectedTypes.length,
                })}
                options={options}
            />

            <Dropdown.Content hasPortal align="start">
                <Dropdown.Reset label={t(keys.reset)} />
                {options.map((type) => (
                    <Dropdown.SelectableItem key={type.id} value={type.id}>
                        <Dropdown.Checkbox />
                        <Dropdown.ItemIcon name={type.icon} size="md" />
                        <Dropdown.ItemText>{t(type.name)}</Dropdown.ItemText>
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};
